import { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { RootReducer } from 'redux/reducers';
import { getFrameDisplayBounds } from 'redux/db/util';
import { DEFAULT_CANVAS_DROP_SHADOW_MODE, selectActiveScene } from 'redux/db/reducers';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { Rect } from 'dragonmouse-shared/geometry';
import * as S from './styles';
import * as Base from '../styles';
import FrameLoadingIndicator from 'components/Widget/FrameLoadingIndicator';
import { useImageLoading } from 'hooks/useImageLoading';
import { useFrameMoonProgress } from 'hooks/useFrameMoonProgress';
import { frameImageUrl } from 'utils';

interface Props {
	frame: Frame;
}

// Scene-coord sizes — scale naturally with the canvas transform.
const MOON_SIZE = 100;
const STATUS_SIZE = 32;
const STATUS_GAP = 40;

// Most frames are above these thresholds and get the constant sizes above.
// Below each threshold, the corresponding dimension scales linearly toward 0
// so a too-small frame doesn't get a cropped moon or overflowing caption.
const WIDTH_SCALE_THRESHOLD = 400;
const HEIGHT_SCALE_THRESHOLD = 350;

//------------------------------------------------------------------------------------------------
// FRAMEVIEW
//------------------------------------------------------------------------------------------------

const FrameView = ({ frame }: Props) => {
	const [bounds, setBounds] = useState<Rect>({ x: 0, y: 0, width: 0, height: 0 });
	const imageSrc = frameImageUrl(frame);
	const [isImageLoading, handleImageRef] = useImageLoading(imageSrc ?? '');

	const scene = useSelector(
		(r: RootReducer) => selectActiveScene(r.db, r.mode.activeSceneId),
		shallowEqual
	);

	const isOverlayVisible = frame.metadata.status !== FrameStatus.READY || isImageLoading;

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------

	// Held here rather than inside the indicator: the overlay unmounts for a beat when the frame
	// turns READY before its new <img> mounts, which would throw away the accumulated progress.
	const moon = useFrameMoonProgress(frame, isImageLoading);

	useEffect(() => {
		setBounds(getFrameDisplayBounds(frame));
	}, [frame.x, frame.y, frame.width, frame.height, frame.metadata?.displayScale]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	// Width drives font size; height drives the moon-to-caption gap; moon takes
	// the smaller of the two so it fits both dimensions. Above the thresholds
	// every scale is clamped to 1 — i.e. the constant sizes above are unchanged.
	const widthScale = Math.min(1, frame.width / WIDTH_SCALE_THRESHOLD);
	const heightScale = Math.min(1, frame.height / HEIGHT_SCALE_THRESHOLD);
	const moonScale = Math.min(widthScale, heightScale);
	const moonSize = MOON_SIZE * moonScale;
	const statusFontSize = STATUS_SIZE * widthScale;
	const statusGap = STATUS_GAP * heightScale;

	const renderOverlay = () => (
		<S.Overlay>
			<S.CenterStack>
				<FrameLoadingIndicator
					frame={frame}
					isImageLoading={isImageLoading}
					moon={moon}
					moonSize={moonSize}
					fontSize={statusFontSize}
					gap={statusGap}
				/>
			</S.CenterStack>
		</S.Overlay>
	);

	const renderContents = () => {
		if (!imageSrc) return null;
		return (
			<img
				key={imageSrc}
				ref={handleImageRef}
				src={imageSrc}
				width={bounds.width}
				height={bounds.height}
			/>
		);
	};

	return (
		<>
			<S.Base sceneBounds={bounds} backgroundColor={frame.metadata.backgroundColor}>
				{renderContents()}
				{isOverlayVisible && renderOverlay()}
			</S.Base>
			<Base.DropShadow
				sceneBounds={bounds}
				dropShadowMode={scene?.metadata?.canvasDropShadowMode || DEFAULT_CANVAS_DROP_SHADOW_MODE}
			/>
		</>
	);
};

export default FrameView;
